import type {
  FleetConfigResponse,
  FleetOwnerThanks,
  FleetRun,
  FleetRunner,
  FleetRunnerKind,
  FleetRunners,
  FleetStoppedRunner
} from './claude-fleet.types'
import { runActivityLabel } from './claude-fleet.describe'
import type { RunnerUsageLine } from './claude-fleet.runner-live'
import { runDurationMs } from './claude-fleet.utils'

import { HIDDEN_PERSON_HELP, isHiddenPerson, personLabel } from '@/services/claude-status'

import { formatCompactRelativeTime, formatDateTime } from '@/utils/dateUtils'
import { formatDurationMs } from '@/utils/format-duration'
import { newestFirst } from '@/utils/newest-first'

/** The fleet's word for a runner owner the viewer may not see. */
const HIDDEN_OWNER = 'owner hidden'

/**
 * How the page reads laptop runners: the Runners panel's rows, and where a run executed. A run record
 * written before laptops could be runners has no `ranOn`, and every such run ran on AgentCore.
 */


export const FLEET_RUNNER_KIND_LABELS: Record<FleetRunnerKind, string> = {
  'pr-review': 'Reviews',
  'pr-fix': 'Fixes',
  'mention-sweep': 'Mentions',
  'qa-verify': 'QA',
  'release-verify': 'Release verify',
  'lpe-loop': 'Delivery-lane ticks',
  'fixme-sweep': 'Test-suite sweeps',
  'qa-fields-audit': 'QA fields audits',
  'lane-size': 'Queue sizing',
  cover: 'Coverage gaps'
}

export function normalizeRunnersReply(reply: Partial<FleetRunners>): FleetRunners {
  return {
    runners: reply.runners ?? [],
    stopped: reply.stopped ?? [],
    totals: reply.totals ?? { live: 0, itemsToday: 0, savedUsdToday: 0 },
    owners: reply.owners ?? [],
    ...(reply.defaults ? { defaults: reply.defaults } : {}),
    viewerEngineer: typeof reply.viewerEngineer === 'boolean' ? reply.viewerEngineer : null
  }
}

/** Whether a runner owner is the given email, ignoring case and spaces; never for an empty email. */
export function isRunnerOwner(owner: string, email: string | null | undefined): boolean {
  const wanted = email?.trim().toLowerCase()
  return Boolean(wanted) && owner.toLowerCase() === wanted
}

/** This engineer's laptop runs today and this week, or null when the fleet sends none for them. */
export function ownerThanks(owners: FleetOwnerThanks[] | undefined, email: string): FleetOwnerThanks | null {
  return owners?.find(row => isRunnerOwner(row.owner, email)) ?? null
}

/** Whether the signed-in engineer has a laptop runner among those the fleet saw in the last 24 hours. */
export function hasOwnRunner(runners: Pick<FleetRunner, 'owner'>[], email: string | null | undefined): boolean {
  return runners.some(runner => isRunnerOwner(runner.owner, email))
}

/** "Went offline 7 min ago" for a laptop whose heartbeat stopped; empty while it is live or the fleet says nothing. */
export function runnerOfflineText(runner: Pick<FleetRunner, 'live' | 'offline'>): string {
  if (runner.live || !runner.offline?.text) return ''
  const text = runner.offline.text.trim()
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

export interface FleetStoppedRunnerView {
  machine: string
  /** The owner's name for ClaudePersonName, which shows a hidden one as "A staff member" with the reason. */
  who: string
  /** "Last seen 9d ago · last took work 9d ago"; the exact time is `seenTitle`, for its hover. */
  seen: string
  seenTitle: string
  why: string
}

/** One row of the Runners panel's Stopped list. */
export function stoppedRunnerView(runner: FleetStoppedRunner): FleetStoppedRunnerView {
  const worked = runner.lastWorkedAt && runner.lastWorkedAt !== runner.lastSeen
    ? ` · last took work ${formatCompactRelativeTime(runner.lastWorkedAt)}`
    : ''
  return {
    machine: runner.machine,
    who: runnerOwnerLabel(runner),
    seen: `Last seen ${formatCompactRelativeTime(runner.lastSeen)}${worked}`,
    seenTitle: `Last seen ${formatDateTime(runner.lastSeen)}`,
    why: runner.why
  }
}

export interface FleetRunPlaceView {
  label: string
  /** The label with the owner's first name only, for a chip in a narrow column. */
  short: string
  /** The runnerOwner's email, for the chip's hover; empty for AgentCore. */
  title: string
}

/** A run executed on a laptop runner, which has no AgentCore session to watch, message or take over. */
export function isLaptopRun(run: Pick<FleetRun, 'ranOn'> | null | undefined): boolean {
  return run?.ranOn === 'laptop'
}

/** "Pat Lee's runner (MacBook Pro)" for a runner's run, the words its PR verdict uses, else "AgentCore"; `short` is
 *  "Pat's machine", for a chip. The machine is its friendly name, else its fleet id, else left out. */
export function describeRunPlace(
  run: Pick<FleetRun, 'ranOn' | 'runnerOwner' | 'runnerOwnerName' | 'runnerMachine' | 'runnerLabel'>
): FleetRunPlaceView {
  if (!isLaptopRun(run)) return { label: 'AgentCore', short: 'AgentCore', title: '' }
  const named = run.runnerOwnerName || run.runnerOwner
  const isPlaceholder = !named || named === HIDDEN_OWNER || isHiddenPerson(named)
  const who = isPlaceholder ? (named ? 'a staff member' : 'an engineer') : named
  const first = who.includes('@') ? who.split('@')[0] : who.split(' ')[0]
  return {
    label: `${who}'s ${machineOf(run)}`,
    short: `${isPlaceholder ? who : first}'s machine`,
    title: run.runnerOwner ?? ''
  }
}

function machineOf(run: Pick<FleetRun, 'runnerMachine' | 'runnerLabel'>): string {
  const machine = run.runnerLabel || run.runnerMachine
  return machine ? `runner (${machine})` : 'machine'
}

/** A runner's owner, with a hidden one in the words ClaudePersonName reads as "A staff member". */
export function runnerOwnerLabel(runner: Pick<FleetRunner, 'name' | 'owner'>): string {
  const name = runner.name || runner.owner
  return name === HIDDEN_OWNER || isHiddenPerson(name) ? '(hidden)' : name
}

/** The machine's model for the line under its owner; null when the laptop sends none. */
export function runnerModelLabel(runner: Pick<FleetRunner, 'model'>): string | null {
  return runner.model?.trim() || null
}

export function runnerKindsLabel(kinds: FleetRunnerKind[]): string {
  return kinds.map(kind => FLEET_RUNNER_KIND_LABELS[kind] ?? kind).join(', ') || '—'
}

/** The most kinds a summary names as missing before it lists what the runner takes instead. */
const KINDS_EXCEPT_MAX = 2

/**
 * The Takes column: "All kinds", "All except Queue sizing", else the list. Most runners take nearly every
 * kind, and the full list pushed Capacity and Version off a laptop-width table; the hover keeps every kind.
 */
export function runnerKindsSummary(kinds: FleetRunnerKind[]): string {
  const missing = (Object.keys(FLEET_RUNNER_KIND_LABELS) as FleetRunnerKind[]).filter(kind => !kinds.includes(kind))
  if (missing.length === 0) return 'All kinds'
  if (missing.length <= KINDS_EXCEPT_MAX && kinds.length > missing.length) {
    return `All except ${missing.map(kind => FLEET_RUNNER_KIND_LABELS[kind]).join(' and ')}`
  }
  return runnerKindsLabel(kinds)
}

/** Why a live laptop takes no work ("AWS SSO has expired"); null for an offline one or one that is only busy. */
export function runnerPausedReason(runner: Pick<FleetRunner, 'live' | 'pausedReason'>): string | null {
  const reason = runner.pausedReason?.trim()
  return runner.live && reason ? reason : null
}

/** "Paused — AWS SSO has expired" for a live laptop that reports why it takes no work; null shows its free slots. */
export function runnerPausedLabel(
  runner: Pick<FleetRunner, 'live' | 'pausedReason'>
): string | null {
  const reason = runnerPausedReason(runner)
  return reason ? `Paused — ${reason}` : null
}

/**
 * Today's runs split in two: the fleet's work, which the daily cap counts, and the reviews and fixes of the owner's own
 * PRs (`thanks.today.own`, absent from an older fleet, which reads as all fleet work).
 */
export function runnerTodaySplit(runner: Pick<FleetRunner, 'itemsToday' | 'thanks'>): { fleet: number; own: number } {
  const own = runner.thanks?.today.own ?? 0
  return { fleet: Math.max(0, runner.itemsToday - own), own }
}

/** "12 of 100" while the laptop has a daily cap, else the count: the fleet's part, since that is what the cap counts. */
export function runnerItemsTodayLabel(runner: Pick<FleetRunner, 'itemsToday' | 'cap' | 'thanks'>): string {
  const { fleet } = runnerTodaySplit(runner)
  return typeof runner.cap === 'number' ? `${fleet} of ${runner.cap}` : String(fleet)
}

/** "81 yours", the reviews and fixes of the owner's own PRs beside the fleet's count; null with none. */
export function runnerOwnTodayLabel(runner: Pick<FleetRunner, 'itemsToday' | 'thanks'>): string | null {
  const { own } = runnerTodaySplit(runner)
  return own > 0 ? `${own} yours` : null
}

/**
 * "34 fleet · 81 yours" while the owner's own PRs ran today, so the first count reads as the fleet's part and not a
 * total the second is part of; null with none, for the caller's own wording of the fleet count.
 */
export function runnerFleetAndOwnLabel(runner: Pick<FleetRunner, 'itemsToday' | 'cap' | 'thanks'>): string | null {
  const { own } = runnerTodaySplit(runner)
  return own > 0 ? `${runnerItemsTodayLabel(runner)} fleet · ${own} yours` : null
}

/** What a laptop's runs would have cost on AgentCore, as an estimate; the column or line says "saved". */
export function savedLabel(savedUsd: number): string {
  return `~$${savedUsd.toFixed(2)}`
}

/** Each known pause: the words its reason contains, a word or two for the sidebar card, and a sentence for a hover. */
const PAUSE_REASONS: { pattern: RegExp; short: string; help: string }[] = [
  {
    pattern: /GB free/i,
    short: 'Low disk',
    help: 'The machine is under its free-disk floor (15 GB by default), so it takes no work until space is freed.'
  },
  {
    pattern: /sso/i,
    short: 'SSO expired',
    help: 'AWS SSO has expired on the machine. The runner sends its owner a sign-in link in Slack; they can also run aws sso login on it.'
  },
  {
    pattern: /vpn|not reachable/i,
    short: 'VPN off',
    help: 'Dev and staging are not reachable from the machine: its VPN is off.'
  },
  {
    pattern: /fleet settings/i,
    short: 'Fleet hold',
    help: 'The fleet settings pause runners or limit what this one may take.'
  },
  {
    pattern: /cap|items a day/i,
    short: 'Cap reached',
    help: 'The runner reached its daily cap; it starts again tomorrow.'
  }
]

function knownPause(reason: string | undefined) {
  const text = reason?.trim() ?? ''
  return { text, known: PAUSE_REASONS.find(({ pattern }) => pattern.test(text)) }
}

/** Why a paused laptop takes no work, in a word or two that fit the sidebar card: "Low disk", else "Paused". */
export function runnerPausedShort(reason: string | undefined): string {
  return knownPause(reason).known?.short ?? 'Paused'
}

/** Why a paused laptop takes no work, in a sentence, for the hover on its "Paused —" label. */
export function runnerPausedHelp(reason: string | undefined): string {
  const { text, known } = knownPause(reason)
  return known?.help ?? text
}

/** One laptop runner as the Runners panel shows it, in its table or, on a phone, as a card. */
export interface FleetRunnerRowView {
  machine: string
  live: boolean
  offlineText: string
  offlineSince?: string
  owner: string
  ownerLink: string
  ownerTitle: string
  model: string | null
  runner: FleetRunner
  kinds: string
  kindsTitle: string
  actionStatus: string | null
  itemsToday: string
  saved: string
  /** What it is doing now, one line; null while offline or saying nothing. */
  doing: string | null
  usage: RunnerUsageLine[]
}

/** "Pat Lee's machine", or "A staff member's machine" for an owner this viewer may not see. */
export function runnerLaptopLabel(runner: Pick<FleetRunner, 'name' | 'owner'>): string {
  return `${personLabel(runnerOwnerLabel(runner))}'s machine`
}

/** The owner link's hover: what it opens, then whose laptop; an owner this viewer may not see says who can. */
export function runnerOwnerTitle(runner: Pick<FleetRunner, 'name' | 'owner' | 'machine' | 'lastSeen'>): string {
  const seen = `${runner.machine} · last seen ${formatDateTime(runner.lastSeen)}`
  const whose = isHiddenPerson(runnerOwnerLabel(runner)) ? `${seen}\n${HIDDEN_PERSON_HELP}` : `${runner.owner} · ${seen}`
  return `See what this runner has been doing: its runs and what it reported\n${whose}`
}

/** One finished run in a runner's drawer: what it worked on, when and for how long. Its outcome chip reads the run itself. */
export interface FleetRunnerHistoryRow {
  run: FleetRun
  activity: string
  started: string
  startedTitle: string
  duration: string
}

export function runnerHistoryRows(
  runs: FleetRun[],
  describe: FleetConfigResponse['describe'] | undefined
): FleetRunnerHistoryRow[] {
  return newestFirst(runs, run => run.startedAt).map(run => {
    const durationMs = runDurationMs(run)
    return {
      run,
      activity: runActivityLabel(run, describe),
      started: formatCompactRelativeTime(run.startedAt),
      startedTitle: `Started ${formatDateTime(run.startedAt)}`,
      duration: durationMs === null ? '—' : formatDurationMs(durationMs)
    }
  })
}

/** The Runs tab's Runner filter: each laptop seen in the last day, and the one a link names even once it has gone. */
export function runnerFilterOptions(
  runners: Pick<FleetRunner, 'machine' | 'name' | 'owner'>[],
  selected: string | null | undefined
): { title: string; value: string }[] {
  const options = runners.map(runner => ({ title: runnerLaptopLabel(runner), value: runner.machine }))
  if (selected && !options.some(option => option.value === selected)) options.push({ title: selected, value: selected })
  return options
}
